import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchActiveHeroes } from '../api';
import { useNavigate } from 'react-router-dom';
import whatsappIcon from '../../assets/images/whatsapp_icon.svg';
import calender from '../../assets/images/calander_plugin.svg';

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

type Slide = {
  _id: string;
  title: { en: string; ar: string };
  image: string;
  priority: number;
  isActive: boolean;
};

const HeroSlider: React.FC = () => {
  const [slides, setSlides] = useState<Slide[]>([]);
  const [realIndex, setRealIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const loadHeroes = async () => {
      try {
        const data = await fetchActiveHeroes();
        const sortedSlides = (data.heroes as Slide[]).sort(
          (a, b) => a.priority - b.priority
        );
        setSlides(sortedSlides);
        setRealIndex(1);
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchSlides'));
        setLoading(false);
      }
    };
    loadHeroes();
  }, [t]);

  const extendedSlides = useMemo(() => {
    return slides.length
      ? [slides[slides.length - 1], ...slides, slides[0]]
      : [];
  }, [slides]);

  useEffect(() => {
    if (extendedSlides.length === 0) return;
    const interval = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setRealIndex((prev) => prev + 1);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [extendedSlides.length, isTransitioning]);

  const handleTransitionEnd = () => {
    if (realIndex === extendedSlides.length - 1) {
      setIsTransitioning(false);
      requestAnimationFrame(() => {
        setRealIndex(1);
        requestAnimationFrame(() => setIsTransitioning(false));
      });
    } else if (realIndex === 0) {
      setIsTransitioning(false);
      requestAnimationFrame(() => {
        setRealIndex(extendedSlides.length - 2);
        requestAnimationFrame(() => setIsTransitioning(false));
      });
    } else {
      setIsTransitioning(false);
    }
  };

  const goToPrevious = () => {
    if (extendedSlides.length > 1 && !isTransitioning) {
      setIsTransitioning(true);
      setRealIndex((prev) => prev - 1);
    }
  };

  const goToNext = () => {
    if (extendedSlides.length > 1 && !isTransitioning) {
      setIsTransitioning(true);
      setRealIndex((prev) => prev + 1);
    }
  };

  // if (loading) return <div>{t('loading')}</div>;
  if (error) return <div>{error}</div>;

  return (
    <>
      <div className="hero-slider-wrapper position-relative">
        <a style={{ cursor: 'pointer' }} onClick={() => navigate('/services')}>
          <div
            className="hero-sliders position-relative"
            style={{ overflow: 'hidden' }}
          >
            <div
              className="sliders d-flex"
              onTransitionEnd={handleTransitionEnd}
              style={{
                width: `${extendedSlides.length * 100}%`,
                transform: `translateX(-${realIndex * (100 / extendedSlides.length)}%)`,
                transition: isTransitioning
                  ? 'transform 1s ease-in-out'
                  : 'none',
              }}
            >
              {extendedSlides.map((slide, index) => (
                <div
                  key={`${slide._id}-${index}`}
                  style={{
                    width: `${100 / extendedSlides.length}%`,
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <img
                    src={`${IMAGE_BASE_URL}/${slide.image}`}
                    alt={
                      i18n.language === 'ar' ? slide.title.ar : slide.title.en
                    }
                    className="slider-image w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                  <div className="slider-content text-center position-absolute top-50 start-50 translate-middle">
                    <h2 className="slider-title text-white mb-3">
                      {i18n.language === 'ar' ? slide.title.ar : slide.title.en}
                    </h2>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </a>
        {/* Prev Button */}
        <button
          className="carousel-control-prev"
          type="button"
          onClick={goToPrevious}
          disabled={isTransitioning}
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            left: '10px',
            zIndex: 10,
            opacity: isTransitioning ? 0.5 : 1,
            pointerEvents: isTransitioning ? 'none' : 'auto',
          }}
        >
          <span
            className="carousel-control-prev-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">{t('previous')}</span>
        </button>

        {/* Next Button */}
        <button
          className="carousel-control-next"
          type="button"
          onClick={goToNext}
          disabled={isTransitioning}
          style={{
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: '10px',
            zIndex: 10,
            opacity: isTransitioning ? 0.5 : 1,
            pointerEvents: isTransitioning ? 'none' : 'auto',
          }}
        >
          <span
            className="carousel-control-next-icon"
            aria-hidden="true"
          ></span>
          <span className="visually-hidden">{t('next')}</span>
        </button>
      </div>
      {/* Consultation Buttons (Moved outside slider container) */}
      <div className="consultation-buttons-container d-flex justify-content-center flex-wrap gap-4 mt-4">
        <a
          href="https://wa.me/971585703027?text=Hi%2C%20I%20would%20like%20to%20book%20a%20consultation."
          target="_blank"
          rel="noopener noreferrer"
          className="consultation-button wp d-flex align-items-center px-4 py-3 border rounded shadow text-decoration-none"
        >
          <img
            src={whatsappIcon}
            alt="WhatsApp"
            className="consultation-icon"
          />
          <div className="consultation-text text-start">
            <div className="fw-bold text-dark">{t('Book_WhatsApp')}</div>
            <div className="fw-bold text-dark">{t('Consultation')}</div>
          </div>
        </a>

        <button
          className="consultation-button book d-flex align-items-center px-4 py-3 border rounded shadow"
          onClick={() => navigate('/appointment')}
        >
          <img src={calender} alt="Calendar" className="consultation-icon" />
          <div className="consultation-text text-start">
            <div className="fw-bold text-dark">{t('Book An Online')}</div>
            <div className="fw-bold text-dark">{t('Consultation')}</div>
          </div>
        </button>
      </div>

      {/* Inline Styles */}
      <style>{`
        .slider-image {
          transition: transform 1s ease-in-out;
          }
          
          .slider-content h2 {
            font-size: 2rem;
            font-weight: bold;
            text-shadow: 0px 2px 6px rgba(0,0,0,0.7);
            }
            
         .consultation-buttons-container {
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;
  bottom: 65px;
  flex-wrap: wrap;
  gap: 1.5rem;
}

.consultation-button {
  min-width: 400px;
  max-width: 420px;
  height: 100px;
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  border-radius: 14px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  background-color: #fff;
}

.consultation-button:hover {
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.consultation-button::before {
  content: "";
  position: absolute;
  top: 0;
  left: -200%;
  width: 150%;
  height: 100%;
  transform: skewX(-25deg);
  transition: left 0.7s ease;
  z-index: 0;
}

.book::before {
  background: linear-gradient(10deg, rgba(255, 55, 55, 0.53), rgba(45, 45, 45, 0.05));
}

.wp::before {
  background: linear-gradient(10deg, rgba(55, 255, 0, 0.52), rgba(71, 71, 71, 0.05));
}

.consultation-button:hover::before {
  left: 0%;
}

.consultation-icon {
  width: 40px;
  height: 40px;
  transition: transform 0.6s ease;
  position: relative;
  z-index: 1;
}

.consultation-text {
  position: relative;
  z-index: 1;
}

/* Mobile & Tablet Styles */
@media (max-width: 768px) {
  .consultation-buttons-container {
    // flex-direction: row; /* keep horizontal layout */
    flex-wrap: wrap;     /* allow wrapping if needed */
    justify-content: center;
    align-items: center;
    bottom: 24px;
    gap: 0.5rem !important;
  }

  .consultation-button {
    width: 100%;
    min-width: unset;
    height: 80px;
    padding: 0.5rem 0.75rem;
    gap: 0.5rem;
  }

  .consultation-icon {
    width: 28px !important;
    height: 28px !important;
  }

  .consultation-text {
    text-align: center;
  }

  .consultation-text .fw-bold {
    font-size: 1.2rem;
    line-height: 1.1;
  }
}
`}</style>
    </>
  );
};

export default HeroSlider;
